import {
  AudioEgressCodec,
  VideoEgressCodec
} from '@tryiris-ai/common.protojs/api/v1/livekit_egress';
import { locateProxy } from '#~/agent/index.js';
import type { Device } from '@studio/models/device';
import type { OmitSyncProps } from '#~/api/types.js';
import type { ControlDevice } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { StudioError } from '#~/error/index.js';

export const updateProfile = async (config: {
  deviceId: Device['id'];
  width: number;
  height: number;
  depth: number;
  frameRate: number;
  audioCodec: AudioEgressCodec;
  audioBitrate: number;
  audioFrequency: number;
  videoCodec: VideoEgressCodec;
  videoBitrate: number;
  keyFrameInterval: number;
}): Promise<void> => {
  // Validate config
  if (!config.deviceId?.trim()) {
    throw new StudioError('Device ID is required');
  }
  if (config.width <= 0 || config.height <= 0) {
    throw new StudioError('Valid width and height are required');
  }
  if (config.frameRate <= 0) {
    throw new StudioError('Valid frame rate is required');
  }
  if (config.videoBitrate <= 0 || config.audioBitrate <= 0) {
    throw new StudioError('Valid bitrate values are required');
  }

  const proxy = locateProxy(config.deviceId);

  if (!proxy) {
    throw new Error('PW-79B');
  }

  // audioQuality and videoQuality are hardcoded to 1 since they are not
  // used in the API, there's no documentation about them.
  // TODO: Remove from api and proto files.
  const mutConfig: OmitSyncProps<Pick<ControlDevice, 'egress'>> = {
    egress: {
      rtmp: {
        rtmpOptions: {
          width: config.width,
          height: config.height,
          depth: config.depth,
          framerate: config.frameRate,
          audioCodec: config.audioCodec,
          audioBitrate: config.audioBitrate,
          audioFrequency: config.audioFrequency,
          videoCodec: config.videoCodec,
          keyFrameInterval: config.keyFrameInterval,
          audioQuality: 1,
          videoQuality: 1,
          videoBitrate: config.videoBitrate
        },
        destinations: {}
      },
      ndiOutput: undefined,
      recording: undefined
    }
  };

  const mutation = {
    control: {
      device: {
        [config.deviceId]: mutConfig
      }
    }
  };

  try {
    await proxy.mutate(mutation, {});
  } catch (cause) {
    throw new StudioError('Unable to update profile settings egress video.', { cause });
  }
};
